import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { User, MapPin, Briefcase, Calendar, Camera, Edit, Save, GraduationCap, Languages, Target } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { formatBioText } from "@/utils/textFormatting";
import { refreshUserProfile } from "@/utils/supabase";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProfilePictureUploader from "@/components/ProfilePictureUploader";
import MenteeReviewsSection from "@/components/MenteeReviewsSection";

const MenteeProfile = () => {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const { id: menteeIdFromUrl } = useParams();
  
  // Determine if we're viewing own profile or someone else's
  const isOwnProfile = !menteeIdFromUrl || (user?.id === menteeIdFromUrl);
  
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    location: "",
    occupation: "",
    bio: "",
    joinDate: new Date().toLocaleDateString(),
    education: "",
    language: "",
    interests: [] as string[],
    profilePicture: "",
    coverPhoto: "/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png",
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [newInterest, setNewInterest] = useState("");
  const [isSaving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const loadUserData = async () => {
      setIsLoading(true);
      setError(null);
      
      // Determine which user ID to use for fetching profile data
      const profileId = menteeIdFromUrl || (user?.id || '');

      console.log("MenteeProfile - Loading profile for ID:", profileId);
      console.log("Current user ID:", user?.id);
      console.log("URL parameter ID:", menteeIdFromUrl);
      console.log("Is own profile:", isOwnProfile);
      
      try {
        // For own profile, we can use stored data first if available
        if (isOwnProfile) {
          const storedUserData = localStorage.getItem("userData");
          if (storedUserData) {
            try {
              const parsedData = JSON.parse(storedUserData);
              setUserData(prevData => ({
                ...prevData,
                ...parsedData,
                interests: parsedData.interests || []
              }));
            } catch (e) {
              console.error("Error parsing user data:", e);
            }
          }
        }
        
        // Only proceed if we have a valid profileId
        if (!profileId) {
          throw new Error("No profile ID available");
        }
        
        // Fetch profile data from Supabase using the appropriate ID
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', profileId)
          .single();
        
        if (error) {
          console.error("Error fetching profile:", error);
          throw new Error(`Failed to load profile: ${error.message}`);
        }
        
        if (!data) {
          throw new Error("Profile not found");
        }

        console.log("Profile data loaded:", data);
        
        // Update state with fetched profile data
        setUserData(prevData => ({
          ...prevData,
          firstName: data.first_name || prevData.firstName,
          lastName: data.last_name || prevData.lastName,
          location: data.location || prevData.location,
          occupation: data.profession || prevData.occupation,
          bio: data.bio || prevData.bio,
          education: data.education || prevData.education,
          language: data.language || prevData.language,
          interests: data.interests || prevData.interests,
          profilePicture: data.avatar_url || prevData.profilePicture,
          coverPhoto: data.coverPhoto || prevData.coverPhoto,
          // Join date could be calculated from created_at if available
          joinDate: data.created_at 
            ? new Date(data.created_at).toLocaleDateString()
            : prevData.joinDate,
        }));

      } catch (e: any) {
        console.error("Error loading user data:", e);
        setError(e.message || "Failed to load profile data");
        toast({
          title: "Error",
          description: e.message || "Failed to load profile data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [user, menteeIdFromUrl, isOwnProfile, toast]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value
    });
  };
  
  const saveProfile = async () => {
    // Only allow saving if viewing own profile
    if (!isOwnProfile) {
      toast({
        title: "Access Denied",
        description: "You can only edit your own profile.",
        variant: "destructive",
      });
      return;
    }
    
    setSaving(true);
    try {
      localStorage.setItem("userData", JSON.stringify(userData));
      
      if (user?.id) {
        console.log("Saving profile to Supabase for user:", user.id);
        console.log("Profile data to save:", {
          first_name: userData.firstName,
          last_name: userData.lastName,
          location: userData.location,
          profession: userData.occupation,
          bio: userData.bio,
          education: userData.education,
          language: userData.language,
        });
        
        const { data, error } = await supabase
          .from('profiles')
          .update({
            first_name: userData.firstName,
            last_name: userData.lastName,
            location: userData.location,
            profession: userData.occupation,
            bio: userData.bio,
            education: userData.education,
            language: userData.language,
            interests: userData.interests,
          })
          .eq('id', user.id);
        
        if (error) {
          console.error("Error updating profile in Supabase:", error);
          toast({
            title: "Error",
            description: "Failed to save profile to database.",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }
        
        toast({
          title: "Success",
          description: "Profile updated successfully!",
          variant: "default",
        });
        
        // Refresh auth context to ensure latest data
        if (refreshUser) {
          await refreshUser();
        }
      }
      
      setIsEditing(false);
    } catch (e) {
      console.error("Error saving profile:", e);
      toast({
        title: "Error",
        description: "An unexpected error occurred while saving.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const addInterest = () => {
    if (newInterest.trim() && !userData.interests.includes(newInterest.trim())) {
      setUserData({
        ...userData,
        interests: [...userData.interests, newInterest.trim()]
      });
      setNewInterest("");
    }
  };

  const removeInterest = (interest: string) => {
    setUserData({
      ...userData,
      interests: userData.interests.filter(item => item !== interest)
    });
  };

  const handleProfilePictureUpdate = (url: string) => {
    setUserData({
      ...userData,
      profilePicture: url
    });
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <div className="flex-grow flex flex-col items-center justify-center p-6">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
            <div className="text-red-500 text-5xl mb-4">!</div>
            <h1 className="text-xl font-semibold mb-2">Error Loading Profile</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      <div className="w-full bg-cover bg-center h-64 sm:h-80 relative" style={{ backgroundImage: `url('${userData.coverPhoto}')` }}>
        <div className="absolute inset-0 bg-emu-blue/20"></div>
        
        {isEditing && isOwnProfile && (
          <Button 
            size="sm" 
            variant="outline"
            className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm"
          >
            <Camera size={16} className="mr-1" />
            Change Cover
          </Button>
        )}
        
        <div className="container mx-auto h-full relative">
          <div className="absolute left-6 -bottom-16 sm:-bottom-20">
            <ProfilePictureUploader
              userId={menteeIdFromUrl || user?.id || ''}
              profilePicture={userData.profilePicture}
              firstName={userData.firstName}
              lastName={userData.lastName}
              onPictureUpdate={handleProfilePictureUpdate}
              disabled={!isEditing || !isOwnProfile}
            />
          </div>
        </div>
      </div>
      
      <main className="flex-grow pt-20 sm:pt-24 pb-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">
                  {isEditing && isOwnProfile ? (
                    <div className="flex gap-2">
                      <Input 
                        name="firstName"
                        value={userData.firstName} 
                        onChange={handleInputChange}
                        placeholder="First Name"
                        className="text-2xl h-auto py-1 font-bold"
                      />
                      <Input 
                        name="lastName"
                        value={userData.lastName} 
                        onChange={handleInputChange}
                        placeholder="Last Name"
                        className="text-2xl h-auto py-1 font-bold"
                      />
                    </div>
                  ) : (
                    `${userData.firstName || "Unknown"} ${userData.lastName || "User"}`
                  )}
                </h1>
                <div className="text-gray-500 flex items-center">
                  <div className="px-3 py-1 rounded-full bg-blue-100 text-emu-blue text-sm font-medium inline-flex items-center mr-3">
                    <User size={14} className="mr-1" />
                    Mentee
                  </div>
                  <div className="flex items-center text-sm">
                    <Calendar size={14} className="mr-1" />
                    Joined {userData.joinDate}
                  </div>
                </div>
              </div>
              
              {isOwnProfile && (
                <Button 
                  onClick={isEditing ? saveProfile : () => setIsEditing(true)}
                  className={`mt-4 sm:mt-0 ${isEditing ? "bg-green-600 hover:bg-green-700" : "bg-emu-blue hover:bg-emu-blue/90"}`}
                  disabled={isSaving}
                >
                  {isEditing ? (
                    <>
                      <Save size={16} className="mr-1" />
                      {isSaving ? "Saving..." : "Save Profile"}
                    </>
                  ) : (
                    <>
                      <Edit size={16} className="mr-1" />
                      Edit Profile
                    </>
                  )}
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <Card className="mb-6">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">About</h2>
                    {isEditing && isOwnProfile ? (
                      <textarea
                        name="bio"
                        value={userData.bio}
                        onChange={handleInputChange}
                        placeholder="Write a short bio about yourself..."
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-base min-h-[150px]"
                      />
                    ) : (
                      <div>
                        {userData.bio ? (
                          formatBioText(userData.bio)
                        ) : (
                          <p className="text-gray-600">No bio provided.</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">General Information</h2>
                    <div className="space-y-4">
                      {isEditing && isOwnProfile ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="location">Location</Label>
                            <div className="relative mt-1">
                              <MapPin size={16} className="absolute left-3 top-3 text-gray-400" />
                              <Input
                                id="location"
                                name="location"
                                value={userData.location}
                                onChange={handleInputChange}
                                placeholder="Your city, country"
                                className="pl-9"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="occupation">Occupation</Label>
                            <div className="relative mt-1">
                              <Briefcase size={16} className="absolute left-3 top-3 text-gray-400" />
                              <Input
                                id="occupation"
                                name="occupation"
                                value={userData.occupation}
                                onChange={handleInputChange}
                                placeholder="Your job title"
                                className="pl-9"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="education">Education</Label>
                            <div className="relative mt-1">
                              <GraduationCap size={16} className="absolute left-3 top-3 text-gray-400" />
                              <Input
                                id="education"
                                name="education"
                                value={userData.education}
                                onChange={handleInputChange}
                                placeholder="Your education"
                                className="pl-9"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="language">Language</Label>
                            <div className="relative mt-1">
                              <Languages size={16} className="absolute left-3 top-3 text-gray-400" />
                              <Input
                                id="language"
                                name="language"
                                value={userData.language}
                                onChange={handleInputChange}
                                placeholder="Languages you speak"
                                className="pl-9"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {userData.location && (
                            <div className="flex items-start">
                              <MapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-500">Location</div>
                                <div className="text-gray-700">{userData.location}</div>
                              </div>
                            </div>
                          )}
                          
                          {userData.occupation && (
                            <div className="flex items-start">
                              <Briefcase className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-500">Occupation</div>
                                <div className="text-gray-700">{userData.occupation}</div>
                              </div>
                            </div>
                          )}
                          
                          {userData.education && (
                            <div className="flex items-start">
                              <GraduationCap className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-500">Education</div>
                                <div className="text-gray-700">{userData.education}</div>
                              </div>
                            </div>
                          )}
                          
                          {userData.language && (
                            <div className="flex items-start">
                              <Languages className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-500">Language</div>
                                <div className="text-gray-700">{userData.language}</div>
                              </div>
                            </div>
                          )}
                          
                          
                          {!userData.location && !userData.occupation && !userData.education && !userData.language && (
                            <p className="text-gray-500 italic">
                              No information provided.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Add Reviews Section */}
                <Card className="mt-6">
                  <CardContent className="p-6">
                    <MenteeReviewsSection menteeId={menteeIdFromUrl || user?.id || ''} />
                  </CardContent>
                </Card>
              </div>
              
              <div>
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">Learning Interests</h2>
                    {isEditing && isOwnProfile ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input 
                            value={newInterest}
                            onChange={(e) => setNewInterest(e.target.value)}
                            placeholder="Add your interests"
                            className="flex-1"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addInterest();
                              }
                            }}
                          />
                          <Button onClick={addInterest} type="button">Add</Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {userData.interests.map((interest, index) => (
                            <Badge key={index} variant="secondary" className="py-1.5 pl-2 pr-1 flex items-center gap-1">
                              {interest}
                              <button 
                                onClick={() => removeInterest(interest)}
                                className="ml-1 text-gray-500 hover:text-gray-700 rounded-full h-4 w-4 flex items-center justify-center"
                              >
                                ✕
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {userData.interests && userData.interests.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {userData.interests.map((interest, index) => (
                              <Badge key={index} variant="secondary" className="py-1.5">
                                {interest}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 italic">
                            No interests added.
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default MenteeProfile;
